import type { Envelope } from "./types";

const DEFAULT_BASE = "http://localhost:8080";
const DEFAULT_KEY = "change-me-local-key";
const WARMING_RETRIES = 4;

export class ApiError extends Error {
  code: string;
  status: number;
  retryAfter: number | null;

  constructor(message: string, code: string, status: number, retryAfter: number | null = null) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
    this.retryAfter = retryAfter;
  }
}

type PublicEnv = {
  BUN_PUBLIC_API_BASE_URL?: string;
  BUN_PUBLIC_API_KEY?: string;
};

function publicEnv(): PublicEnv {
  return (import.meta.env ?? {}) as PublicEnv;
}

export function getApiBase(): string {
  const raw = publicEnv().BUN_PUBLIC_API_BASE_URL || DEFAULT_BASE;
  return raw.replace(/\/$/, "");
}

export function getApiKey(): string {
  return publicEnv().BUN_PUBLIC_API_KEY || DEFAULT_KEY;
}

export function messageFor(code: string, retryAfter: number | null = null): string {
  switch (code) {
    case "unauthorized":
      return "Unauthorized — check BUN_PUBLIC_API_KEY.";
    case "rate_limited":
      return retryAfter
        ? `Rate limited. Retry after ${retryAfter}s.`
        : "Rate limited. Wait before retrying.";
    case "warming":
      return "Cache still filling";
    case "unavailable":
      return "API unavailable.";
    case "redis_unavailable":
      return "Redis unavailable.";
    case "invalid_id":
      return "Invalid id.";
    case "same_station":
      return "From and to cannot be the same station.";
    case "not_found":
      return "Not found.";
    case "internal":
      return "Internal error.";
    default:
      return code || "Request failed.";
  }
}

function httpToCode(status: number): string {
  switch (status) {
    case 401:
      return "unauthorized";
    case 404:
      return "not_found";
    case 400:
      return "invalid_id";
    case 429:
      return "rate_limited";
    case 503:
      return "unavailable";
    default:
      return "internal";
  }
}

function parseRetryAfter(header: string | null): number | null {
  if (!header) return null;
  const n = Number(header);
  return Number.isFinite(n) ? n : null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function fetchJSON<T>(path: string, options?: { retryWarming?: boolean }): Promise<T> {
  const retryWarming = options?.retryWarming !== false;
  let attempt = 0;

  while (true) {
    const url = `${getApiBase()}${path.startsWith("/") ? path : `/${path}`}`;
    let res: Response;
    try {
      res = await fetch(url, {
        headers: { "x-api-key": getApiKey() },
      });
    } catch (err) {
      throw new ApiError(
        `Could not reach API at ${getApiBase()}. ${err instanceof Error ? err.message : String(err)}`,
        "unavailable",
        0,
      );
    }

    const retryAfter = parseRetryAfter(res.headers.get("Retry-After"));
    const text = await res.text();
    let body: Envelope<T> | null = null;
    if (text) {
      try {
        body = JSON.parse(text) as Envelope<T>;
      } catch {
        body = null;
      }
    }

    if (body?.success === true) {
      return body.data as T;
    }

    const code = body?.error || httpToCode(res.status);

    if (code === "warming" && retryWarming && attempt < WARMING_RETRIES) {
      const delay = Math.min(1000 * 2 ** attempt, 8000);
      attempt += 1;
      await sleep(delay);
      continue;
    }

    throw new ApiError(messageFor(code, retryAfter), code, res.status, retryAfter);
  }
}

export type HealthState = {
  healthz: boolean;
  readyz: boolean;
  warming: boolean;
  down: boolean;
};

export async function checkHealth(): Promise<HealthState> {
  const base = getApiBase();
  const probe = async (path: string) => {
    try {
      const res = await fetch(`${base}${path}`);
      return { ok: res.ok, status: res.status };
    } catch {
      return { ok: false, status: 0 };
    }
  };

  const [healthz, readyz] = await Promise.all([probe("/healthz"), probe("/readyz")]);
  const warming = healthz.status === 503 || readyz.status === 503;
  const down = !healthz.ok && !readyz.ok && !warming;

  return {
    healthz: healthz.ok,
    readyz: readyz.ok,
    warming,
    down,
  };
}
